/**
 * Dialog for starting another activity alongside a running session, such as a lecture during a
 * run. Lists open, scheduled activities that are not already running; the owner starts the chosen
 * one on the server and opens its panel.
 *
 * @module apps/web/src/components/immersion/AddConcurrentActivityDialog
 */

import { useMemo, useState } from 'react';
import type { TaskUI } from '@fluxability/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { isOpenActivity } from '@/components/activities/ConcurrentActivityPicker';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import { Loader2, Play } from 'lucide-react';

/** Visibility, the running activity, and the start action for a concurrent activity. */
interface AddConcurrentActivityDialogProps {
  /** Controlled visibility. */
  open: boolean;
  /** Request a visibility change. */
  onOpenChange: (open: boolean) => void;
  /** Activity whose session is running; it is never offered. */
  currentTaskId: string;
  /** Start the chosen activity concurrently; resolves when the start finished or failed. */
  onStart: (task: TaskUI) => Promise<void>;
}

/** Rows shown before the person narrows the list by typing. */
const VISIBLE_ROWS = 50;

/** Render the searchable list of activities that can start alongside the running session. */
export function AddConcurrentActivityDialog({
  open,
  onOpenChange,
  currentTaskId,
  onStart,
}: AddConcurrentActivityDialogProps) {
  const tasks = useTasksStore(s => s.tasks);
  const activeTaskIds = useScheduleStore(s => s.activeImmersionTaskIds);
  const [query, setQuery] = useState('');
  const [startingId, setStartingId] = useState<string | null>(null);
  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tasks
      .filter(
        task =>
          task.id !== currentTaskId &&
          !activeTaskIds.includes(task.id) &&
          isOpenActivity(task) &&
          Boolean(task.scheduledStart && task.scheduledEnd)
      )
      .filter(task => !needle || task.title.toLowerCase().includes(needle))
      .slice(0, VISIBLE_ROWS);
  }, [tasks, activeTaskIds, currentTaskId, query]);

  /** Start one activity, keeping the dialog open on failure so the person can pick again. */
  async function start(task: TaskUI) {
    setStartingId(task.id);
    try {
      await onStart(task);
    } finally {
      setStartingId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add concurrent activity</DialogTitle>
          <DialogDescription>
            Start another activity without ending this one. Both sessions record the time you spend
            together.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search scheduled activities"
          aria-label="Search scheduled activities"
        />
        <div className="native-scroll max-h-72 overflow-y-auto rounded-md border">
          {candidates.length === 0 ? (
            <p className="text-muted-foreground p-3 text-sm">
              No other scheduled activity is available. To record something you already did at the
              same time, use Log activity session instead.
            </p>
          ) : (
            candidates.map(task => (
              <div
                key={task.id}
                className="flex min-h-11 items-center gap-2 border-b px-3 py-1.5 last:border-b-0"
              >
                <span className="min-w-0 flex-1 truncate text-sm">{task.title}</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void start(task)}
                  disabled={startingId !== null}
                  aria-label={`Start ${task.title} concurrently`}
                >
                  {startingId === task.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
